import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import HeaderVolver from "../HeaderVolver/HeaderVolver";
import useStore from '../../useStore';
import { 
  obtenerRecomendaciones, 
  obtenerRecomendacionPorId, 
  importarRecomendacion,
  eliminarRecomendacion,
  obtenerMisRecomendaciones,
  obtenerPerfil
} from '../../repositorios/apiServer';
import style from './Recomendaciones.module.css';

export default function Recomendaciones() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const userToken = useStore((state) => state.userToken);
  const [userData, setUserData] = useState(null);
  const [recomendaciones, setRecomendaciones] = useState([]);
  const [misRecomendaciones, setMisRecomendaciones] = useState([]);
  const [recomendacionDestacada, setRecomendacionDestacada] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [procesandoCompartido, setProcesandoCompartido] = useState(false);
  const [accionEnProceso, setAccionEnProceso] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  
  useEffect(() => {
    if (userToken) {
      const redirectURL = localStorage.getItem('redirectAfterLogin');
      if (redirectURL) {
        localStorage.removeItem('redirectAfterLogin');
        setTimeout(() => {
          navigate(redirectURL, { replace: true });
        }, 100);
      }
    }
  }, [userToken, navigate]);

  const esVistaCompartida = useMemo(() => 
    location.pathname.includes('/recomendaciones/compartido/') || 
    new URLSearchParams(location.search).has('id'),
  [location.pathname, location.search]);

  const cargarPerfil = useCallback(async () => {
    if (userToken && !userData) {
      try {
        const perfilData = await obtenerPerfil();
        
        if (!isMounted) return;
        
        if (perfilData && perfilData.user) {
          setUserData(perfilData.user);
        }
      } catch (error) {
        console.error("Error al cargar perfil:", error);
      }
    }
  }, [userToken, userData, isMounted]);

  useEffect(() => {
    cargarPerfil();
  }, [cargarPerfil]);

  const cargarRecomendacionPorId = useCallback(async (id) => {
    try {
      if (!id) {
        console.error("ID de recomendación no proporcionado");
        return;
      }
      
      setCargando(true);
      
      const data = await obtenerRecomendacionPorId(id);
      
      if (!isMounted) return;
      
      if (data && data._id) {
        setRecomendacionDestacada(data);
      } else {
        console.error("La recomendación no tiene el formato esperado:", data);
        setError("No se pudo cargar la recomendación. Formato de datos incorrecto.");
      }
    } catch (error) {
      console.error('Error al cargar recomendación destacada:', error);
      if (isMounted) {
        setError('No se pudo cargar la recomendación solicitada. Por favor, verifica el enlace e inténtalo de nuevo.');
      }
    } finally {
      if (isMounted) {
        setCargando(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    const procesarRutaCompartida = async () => {
      if (location.pathname.includes('/recomendaciones/compartido/')) {
        try {
          setProcesandoCompartido(true);
          
          const recomendacionId = params.id;
          
          if (recomendacionId) {
            await cargarRecomendacionPorId(recomendacionId);
          } else {
            setError("El enlace compartido no es válido o está incompleto.");
          }
        } catch (error) {
          console.error("Error al procesar ruta compartida:", error);
          if (isMounted) {
            setError("Hubo un problema al cargar la recomendación compartida.");
          }
        } finally {
          if (isMounted) {
            setProcesandoCompartido(false);
          }
        }
      }
    };

    procesarRutaCompartida();
  }, [location.pathname, params, cargarRecomendacionPorId, isMounted]);

  useEffect(() => {
    const procesarParametrosURL = async () => {
      if (!location.pathname.includes('/recomendaciones/compartido/')) {
        try {
          const queryParams = new URLSearchParams(location.search);
          const recomendacionId = queryParams.get('id');
          
          if (recomendacionId) {
            await cargarRecomendacionPorId(recomendacionId);
          }
        } catch (error) {
          console.error("Error al procesar parámetros de URL:", error);
          if (isMounted) {
            setError("Hubo un problema al cargar la recomendación desde el enlace.");
          }
        }
      }
    };

    procesarParametrosURL();
  }, [location.search, cargarRecomendacionPorId, location.pathname, isMounted]);

  const cargarMisRecomendaciones = useCallback(async () => {
    if (!userToken || !userData) return;
    
    try {
      const userId = userData.id || userData._id;
      
      const data = await obtenerMisRecomendaciones(userId);
      
      if (!isMounted) return;
      
      if (data && Array.isArray(data)) {
        const idsRecomendaciones = data.map(rec => rec._id);
        setMisRecomendaciones(idsRecomendaciones);
      } else {
        setMisRecomendaciones([]);
      }
    } catch (error) {
      console.error('Error al cargar mis recomendaciones:', error);
    }
  }, [userToken, userData, isMounted]);

  const cargarRecomendaciones = useCallback(async () => {
    try {
      setCargando(true);
      
      const data = await obtenerRecomendaciones();
      
      if (!isMounted) return;
      
      if (data && Array.isArray(data)) {
        setRecomendaciones(data);
      } else {
        setRecomendaciones([]);
      }
    } catch (error) {
      console.error('Error al cargar recomendaciones:', error);
      if (isMounted) {
        setError('No se pudieron cargar las recomendaciones. Inténtalo de nuevo más tarde.');
      }
    } finally {
      if (isMounted) {
        setCargando(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    const inicializarRecomendaciones = async () => {
      if (!userToken && !esVistaCompartida) {
        navigate('/login');
        return;
      }

      if (userToken && userData) {
        await Promise.all([
          cargarRecomendaciones(),
          cargarMisRecomendaciones()
        ]);
      } else if (userToken) {
        await cargarRecomendaciones();
      }
    };

    inicializarRecomendaciones();
  }, [userToken, userData, esVistaCompartida, navigate, cargarRecomendaciones, cargarMisRecomendaciones]);

  const tengoRecomendacion = useCallback((recomendacionId) => {
    return misRecomendaciones.includes(recomendacionId);
  }, [misRecomendaciones]);

  const handleToggleRecomendacion = useCallback(async (recomendacion) => {
    if (!userToken || !userData) {
      const currentPath = `${location.pathname}${location.search}`;
      localStorage.setItem('redirectAfterLogin', currentPath);
      navigate('/login');
      return;
    }
  
    try {
      setAccionEnProceso(true);
      const recomendacionId = recomendacion._id;
      const yaLaTengo = tengoRecomendacion(recomendacionId);
      
      if (yaLaTengo) {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta recomendación?')) {
          const userId = userData.id || userData._id;
          
          await eliminarRecomendacion(recomendacionId, userId);
          
          if (!isMounted) return;
          
          setMisRecomendaciones(prevState => 
            prevState.filter(id => id !== recomendacionId)
          );
          
          alert('Recomendación eliminada correctamente');
        }
      } else {
        const userId = userData.id || userData._id;
        
        await importarRecomendacion(recomendacionId, userId);

        if (!isMounted) return;
        
        setMisRecomendaciones(prevState => [...prevState, recomendacionId]);

        alert('Recomendación añadida correctamente');
      }

      await Promise.all([
        cargarRecomendaciones(),
        cargarMisRecomendaciones()
      ]);
      
    } catch (error) {
      console.error('Error al modificar la recomendación:', error);
      if (isMounted) {
        alert('Hubo un problema al procesar la recomendación. Inténtalo de nuevo.');
      }
    } finally {
      if (isMounted) {
        setAccionEnProceso(false);
      }
    }
  }, [userToken, userData, navigate, location, tengoRecomendacion, isMounted, cargarRecomendaciones, cargarMisRecomendaciones]);

  const formatearFecha = useCallback((fechaString) => {
    if (!fechaString) return 'Fecha no disponible';
    
    try {
      const fecha = new Date(fechaString);
      return fecha.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      });
    } catch (error) {
      console.error("Error al formatear fecha:", error);
      return fechaString;
    }
  }, []);

  const agregarAlCarrito = useCallback((productos) => {
    if (!productos || productos.length === 0) {
      console.warn("No hay productos para agregar al carrito");
      return;
    }
    
    try {
      const productosCarrito = productos.map(p => ({...p}));
      useStore.getState().setCarritoProductos(productosCarrito);
      navigate('/carta');
    } catch (error) {
      console.error("Error al agregar productos al carrito:", error);
      alert("Hubo un problema al agregar los productos al carrito. Inténtalo de nuevo.");
    }
  }, [navigate]);

  const volverACarta = useCallback(() => {
    navigate('/carta');
  }, [navigate]);

  const compartirRecomendacion = useCallback((recomendacion) => {
    if (!recomendacion || !recomendacion._id) {
      console.error("Recomendación inválida para compartir");
      return;
    }
    
    try {
      const baseUrl = window.location.origin;
      
      const recomendacionUrl = `${baseUrl}/recomendaciones?id=${recomendacion._id}`;
      
      const nombreProductos = recomendacion.productos.map(p => p.nombre || 'producto').join(', ');
      const mensajeCompartir = 
        `¡Mira esta recomendación de ${recomendacion.nombreUsuario} en Romana! 🍽️\n\n` +
        `Productos: ${nombreProductos}\n\n` +
        `👉 ${recomendacionUrl}`;
      
      window.open(`https://wa.me/?text=${encodeURIComponent(mensajeCompartir)}`, '_blank');
      
      
      navigator.clipboard.writeText(recomendacionUrl)
        .then(() => console.log("Enlace copiado al portapapeles"))
        .catch(err => console.error("Error al copiar:", err));
      
    } catch (error) {
      console.error("Error al compartir recomendación:", error);
      alert("Hubo un problema al compartir la recomendación. Inténtalo de nuevo.");
    }
  }, []);

  const handleEliminarRecomendacion = useCallback(async (recomendacion) => {
    if (!userToken || !userData) {
      const currentPath = `${location.pathname}${location.search}`;
      localStorage.setItem('redirectAfterLogin', currentPath);
      navigate('/login');
      return;
    }
  
    try {
      setAccionEnProceso(true);
      
      const recomendacionId = typeof recomendacion === 'object' ? recomendacion._id : recomendacion;
      
      if (!recomendacionId) {
        console.error("ID de recomendación no válido:", recomendacion);
        throw new Error("ID de recomendación no válido");
      }
      
      if (window.confirm('¿Estás seguro de que deseas eliminar esta recomendación?')) {
        const userId = userData.id || userData._id;
        
        await eliminarRecomendacion(recomendacionId, userId);

        if (!isMounted) return;
        
        setRecomendaciones(prevState => 
          prevState.filter(rec => rec._id !== recomendacionId)
        );

        if (recomendacionDestacada && recomendacionDestacada._id === recomendacionId) {
          setRecomendacionDestacada(null);
        }
        
        alert('Recomendación eliminada correctamente');
      }
    } catch (error) {
      console.error('Error al eliminar la recomendación:', error);
      if (isMounted) {
        alert('No se pudo eliminar la recomendación. Inténtalo de nuevo.');
      }
    } finally {
      if (isMounted) {
        setAccionEnProceso(false);
      }
    }
  }, [userToken, userData, navigate, location, recomendacionDestacada, isMounted]);

  return (
    <div className={style.recomendaciones_container}>
      <HeaderVolver
      tituloPrincipal="Recomendaciones" 
      tituloDestacado="Amigos"
      />
      <div className={style.volver_container}>
       <button 
       className={style.boton_volver} 
       onClick={() => navigate("/carta")}
       >
        Volver
       </button>
      </div>      
      
      <div className={style.recomendaciones_content}>
        {procesandoCompartido ? (
          <div className={style.loading_message}>Cargando recomendación compartida...</div>
        ) : cargando ? (
          <div className={style.loading_message}>Cargando recomendaciones...</div>
        ) : error ? (
          <div className={style.error_message}>{error}</div>
        ) : (
          <>
            {!userToken && esVistaCompartida && (
              <div className={style.login_reminder}>
                <p>Estás viendo una recomendación compartida. Para guardarla o ver más recomendaciones, 
                  <button 
                    className={style.login_button}
                    onClick={() => {
                      // Guardar la URL actual completa en localStorage
                      const currentPath = `${location.pathname}${location.search}`;
                      localStorage.setItem('redirectAfterLogin', currentPath);
                      navigate('/login');
                    }}
                  >
                    Inicia sesión
                  </button>
                </p>
              </div>
            )}

            {recomendacionDestacada && (
              <div className={style.recomendacion_destacada}>
                <h2>
                  {esVistaCompartida 
                    ? `Recomendación de ${recomendacionDestacada.nombreUsuario || "Usuario"}` 
                    : " "}
                </h2>
                <div className={style.recomendacion_card_destacada}>
                  <div className={style.productos_lista}>
                    {recomendacionDestacada.productos && recomendacionDestacada.productos.length > 0 ? (
                      recomendacionDestacada.productos.map((producto, index) => (
                        <div key={index} className={style.producto_item}>
                          <div className={style.producto_info}>
                            <span className={style.producto_nombre}>
                              {producto.nombre || "Producto sin nombre"}
                            </span>
                            {producto.cantidad && (
                              <span className={style.producto_cantidad}>
                                x{producto.cantidad}
                              </span>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className={style.no_productos}>
                        No hay productos en esta recomendación
                      </div>
                    )}
                  </div>
                  
                  <div className={style.recomendacion_footer}>
                    <div className={style.recomendacion_actions}>
                      {userToken && (
                        <button 
                          className={`${style.boton_guardar} ${tengoRecomendacion(recomendacionDestacada._id) ? style.boton_eliminar : ''}`}
                          onClick={() => handleToggleRecomendacion(recomendacionDestacada) && navigate('/carta')}
                          disabled={accionEnProceso}
                        >
                          {accionEnProceso ? "Procesando..." : 
                            tengoRecomendacion(recomendacionDestacada._id) ? "Eliminar" : "Añadir"}
                        </button>
                      )}
                      
                      <button 
                        className={style.boton_compartir}
                        onClick={() => compartirRecomendacion(recomendacionDestacada) && navigate('/carta')}
                        disabled={accionEnProceso}
                      >
                        Compartir
                      </button>
                      
                      <button 
                        className={style.boton_pedir}
                        onClick={() => agregarAlCarrito(recomendacionDestacada.productos) }
                        disabled={accionEnProceso}
                      >
                        Pedir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {userToken && !esVistaCompartida && (
              <div className={style.recomendaciones_lista_container}>
                {recomendaciones.length > 0 ? (
                  <div className={style.recomendaciones_lista}>
                    {recomendaciones.map((recomendacion) => (
                      <div key={recomendacion._id} className={style.recomendacion_card}>
                        {/* Añadimos el nombre del usuario que compartió la recomendación */}
                        <div className={style.recomendacion_usuario}>
                          <span className={style.nombre_usuario}>
                            De: {recomendacion.nombreUsuario || "Usuario"}
                          </span>
                        </div>
                        
                        <div className={style.productos_lista}>
                          {recomendacion.productos && recomendacion.productos.length > 0 ? (
                            recomendacion.productos.map((producto, index) => (
                              <div key={index} className={style.producto_item}>
                                <span className={style.producto_nombre}>
                                  {producto.nombre || "Producto sin nombre"}
                                </span>
                                {producto.cantidad && (
                                  <span className={style.producto_cantidad}>
                                    x{producto.cantidad}
                                  </span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className={style.no_productos}>
                              No hay productos en esta recomendación
                            </div>
                          )}
                        </div>
                        
                        <div className={style.recomendacion_footer}>
                          <div className={style.recomendacion_actions}>
                            <button 
                              className={`${style.boton_compartir} ${tengoRecomendacion(recomendacion._id) ? style.boton_eliminar : ''}`}
                              onClick={() => handleEliminarRecomendacion(recomendacion)}
                              disabled={accionEnProceso}
                            >
                              Eliminar
                            </button>
                            
                            <button 
                              className={style.boton_compartir}
                              onClick={() => compartirRecomendacion(recomendacion)}
                              disabled={accionEnProceso}
                            >
                              Compartir
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={style.empty_recomendaciones}>
                    <p>No hay recomendaciones disponibles.</p>
                    <p>¡Comparte tus pedidos para que otros usuarios puedan verlos!</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}